import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AsgardTab } from 'src/app/core/asgard-tab';
import { ToastrActionEnum } from 'src/app/utils/enums';
import { blobToFile } from 'src/app/utils/helpers/files.helprer';
import { BlobToPdfService } from '../blob-to-pdf.service';
import { getMimeTypeEnumFromExt } from '../cloud-storage/common';
import { CurrentExamService } from '../exam/current-exam.service';
import { ExamDocumentType } from '../exam/enums/document-type.enum';
import { ExamDocumentMetadata } from '../exam/interfaces/exam-document-metadata.interface';
import { Exam } from '../exam/interfaces/exam.interface';
import { OpenFileAction, OpenFileService } from '../open-file.service';
import { TabService } from '../tab.service';
import { ToastrDialogService } from '../toastr-dialog.service';
import { ExamService } from './exam.service';
import { CustomTextareaService } from '../text-notes/custom-textarea.service';

interface AdditionalDocument {
  tabId: string; // for testing the exam
  file: File;
}

@Injectable({
  providedIn: 'root'
})
export class ExtraDocumentsRestrictionService implements OnDestroy {
  private MAX_DRAFTS_AMOUNT = 3;

  private readonly subscriptions: Subscription = new Subscription();
  private examInProgress: boolean = false;
  private activeExam: Exam | undefined = undefined;

  isAllowDraft = true;
  isStoreDraft = true;

  private additionalDocuments: AdditionalDocument[] = [];
  private draftTabIds: string[] = [];

  private _examAttachments: ExamDocumentMetadata[] = [];
  private _examFile: BehaviorSubject<File | null> = new BehaviorSubject(null);

  get canAddDraft(): boolean {
    return this.draftTabIds.length < this.MAX_DRAFTS_AMOUNT;
  }

  get examAttachments(): ExamDocumentMetadata[] {
    return this._examAttachments;
  }

  get examFileObservable(): Observable<File | null> {
    return this._examFile;
  }

  get examFile(): File | null {
    return this._examFile.value;
  }

  constructor(
    private readonly currentExamService: CurrentExamService,
    private readonly examService: ExamService,
    private readonly openFileService: OpenFileService,
    private readonly tabService: TabService,
    private readonly blobToPdfService: BlobToPdfService,
    private readonly toastrService: ToastrDialogService,
    private readonly translate: TranslateService,
    private readonly area: CustomTextareaService
  ) {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadAdditionalDocument(): File[] {
    return this.additionalDocuments.map(doc => doc.file);
  }

  saveAdditionalDocuments(files: File[]): void {
    this.additionalDocuments = files.map(file => {
      // tabId can be empty here because you can not edit the list while testing the exam
      // and tabId is only used for testing the exam
      return {tabId: '', file};
    });
  }

  isFileAllowed(file: File): boolean {
    if (file.name.toLowerCase().endsWith('.pdf') && file.type === 'application/pdf') {
      return true;
    } else {
      this.toastrService.showToaster(
        ToastrActionEnum.Error,
        this.translate.instant('TOASTR.ONLYPDF.CONTENT'),
        this.translate.instant('TOASTR.ONLYPDF.TITLE')
      );
      return false;
    }
  }

  loadToolbarAdditionalDocuments(): string[] {
    return this._examAttachments.map(x => x.name);
  }

  async openAdditionalDocument(index: number): Promise<void> {
    if (this.examInProgress && !!this.activeExam) {
      const fileMetadata = this._examAttachments[index];
      const blob = await this.examService.downloadExamFile(this.activeExam.id, fileMetadata.name, fileMetadata.path) as Blob;
      const mime = getMimeTypeEnumFromExt(fileMetadata.name);
      const file = blobToFile(blob, fileMetadata.name, mime.toString());
      this.openFile(file);
    } else {
      this.openAdditionalDocumentItem(this.additionalDocuments[index]);
    }
  }

  openDraft(): void {
    if (this.canAddDraft) {
      // first close notes if there are still open
      // (for normal open tab, the validator check happens before the 'addNewTab()')
      // (but here the check happens after, causing notes to disappear)
      if (this.area.isAreaActive) {
        this.area.removeFocusDoc();
      }

      // const tabName = `${this.translate.instant('COMMON.DRAFT')}${this.draftTabIds.length + 1}`;
      const newDraftTabId = this.tabService.addNewTab().id;
      this.currentExamService.addDraftTabId(newDraftTabId);
    }
  }

  // close all the documents after testing is finished
  closeAllAdditionalDocuments(): void {
    this.additionalDocuments.forEach(doc => {
      this.tabService.closeTab(doc.tabId);
    });
  }

  closeDraft(): void {
    this.currentExamService.draftTabIds.forEach(id => this.tabService.closeTab(id));
    this.currentExamService.setDraftTabIds([]);
  }

  reset(): void {
    this.isAllowDraft = true;
    this.isStoreDraft = true;
    
    this.additionalDocuments = [];
    this._examFile.next(null);
    // this.draftTabIds = [];
  }

  isAttachment(file: File): boolean {
    return this.additionalDocuments.find(x => this.getFileName(x.file) === file.name) !== undefined;
  }

  setExamFile(file: File | null): void {
    this._examFile.next(file);
  }

  wasFileWithSameNameAlreadyAdded(fileName): boolean {
    const files = this.loadAdditionalDocument();
    const fileNames = files.map(x => x.name);
    return fileNames.includes(fileName);
  }

  wasExamWithSameNameAlreadyAdded(fileName): boolean {
    return fileName === this.examFile?.name;
  }

  private getTabName(tab: AsgardTab): string {
    if (tab.file) {
      return tab.file.name;
    }

    if (tab.title) {
      return tab.title;
    }

    return '';
  }

  private getFileName(file: File): string {
    // if the file is rtf or pdf, return that name
    if (file.name.match(/\.rtf$/i) || file.name.match(/\.pdf$/i)) {
      return file.name;
    }

    // if the file name was something else like .docx, .kes, ... return the name + .pdf
    return this.blobToPdfService.replaceExtension(file.name, 'pdf');
  }

  private initSubscriptions() {
    this.subscriptions.add(
      this.currentExamService.inProgressObservable.subscribe(inProgress => this.examInProgress = inProgress)
    );

    this.subscriptions.add(
      this.currentExamService.examMetadataObservable.subscribe(exam => {
        this.activeExam = exam;

        if (exam) {
          this.isAllowDraft = exam.isAllowDraft;
          this.isStoreDraft = exam.isStoreDraft;
          this._examAttachments = exam.examFiles.filter(x => x.documentType == ExamDocumentType.Attachment);
        }
      })
    );

    this.subscriptions.add(
      this.currentExamService.draftTabIdsObservable.subscribe(draftTabIds => this.draftTabIds = draftTabIds)
    );

    this.subscriptions.add(
      this.tabService.closeTabObservable().subscribe((closedTabId: string | null) => {
        if (!!closedTabId) {
          this.currentExamService.setDraftTabIds(this.draftTabIds.filter(x => x !== closedTabId));
        }

        // check if the id is from an additional document (while testing)
        const doc = this.additionalDocuments.find(x => x.tabId === closedTabId);
        if (doc) {
          doc.tabId = '';
        }
      })
    );
  }

  private openFile(file: File): void {
    if (!!file) {
      // check if the document is already open
      const tabs = this.tabService.allTabs;
      const result = tabs.find(tab => this.getTabName(tab) === this.getFileName(file!));
      // if no tab was found
      if (!result) {
        // open the document
        this.openFileService.open(file, OpenFileAction.OPEN);
      }
    }
  }

  private async openAdditionalDocumentItem(doc: AdditionalDocument): Promise<void> {
    // if the document doesn't have a tabId yet, it wasn't opened yet
    if (doc.tabId === '') {
      const id = await this.openFileService.open(doc.file, OpenFileAction.OPEN);
      doc.tabId = id;
    }
  }
}
